/**
 * The app shell: checks the sign-in, then routes (TD §10):
 *   /login      sign-in (password, then "What's your name?")
 *   /           My plans
 *   /new        new plan wizard
 *   /plan/:id   the map editor inside PlanFrame
 * Any 401 shows sign-in and returns to the same page afterwards.
 */
import { useEffect, useState } from 'react';
import { ApiFailure } from './api';
import { checkSession, signOut, useSession } from './store/session';
import { Toaster, errorText } from './ui/shell/dialogs';
import { MyPlans } from './ui/shell/MyPlans';
import { PlanFrame } from './ui/shell/PlanFrame';
import { EditorPage } from './ui/editor/Editor';
import { WizardPage } from './ui/wizard';
import { Link, matchRoute, navigate, useLocation } from './ui/shell/router';
import { SignIn } from './ui/shell/SignIn';
import { getTheme, setTheme, type ThemeChoice } from './ui/shell/theme';

/** The whole app. */
export function App() {
  const session = useSession();
  const { pathname, search } = useLocation();
  const [checkError, setCheckError] = useState('');

  const check = () => {
    setCheckError('');
    checkSession().catch((e) =>
      setCheckError(e instanceof ApiFailure && e.code === 'offline' ? 'Check your connection, then try again.' : errorText(e)),
    );
  };
  useEffect(check, []);

  // Keep the address bar in line with the session: /login only while signed out.
  useEffect(() => {
    if (session.status === 'signedOut' && pathname !== '/login') {
      const next = pathname + search;
      navigate(next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`, { replace: true });
    } else if (session.status === 'signedIn' && pathname === '/login') {
      navigate(nextFrom(search), { replace: true });
    }
  }, [session.status, pathname, search]);

  if (session.status === 'checking') {
    return (
      <main className="page center-page">
        {checkError ? (
          <div className="empty">
            <h2>Can't reach the planner</h2>
            <p>{checkError}</p>
            <button type="button" className="primary" onClick={check}>Try again</button>
          </div>
        ) : (
          <p className="muted">Loading…</p>
        )}
        <Toaster />
      </main>
    );
  }

  if (session.status === 'signedOut') {
    return (
      <>
        <SignIn reason={session.reason} />
        <Toaster />
      </>
    );
  }

  const plan = matchRoute('/plan/:id', pathname);
  let page;
  if (plan) {
    page = (
      <EditorPage key={plan.id} planId={plan.id!} />
    );
  } else if (matchRoute('/new', pathname)) {
    page = <WizardPage />;
  } else if (matchRoute('/', pathname) || pathname === '/login') {
    page = <MyPlans />;
  } else {
    page = (
      <main className="page">
        <div className="empty">
          <h2>Page not found</h2>
          <Link to="/" className="button primary">Go to My plans</Link>
        </div>
      </main>
    );
  }

  return (
    <>
      {!plan && <TopBar name={session.name} />}
      {page}
      <Toaster />
    </>
  );
}

/** Where to go after signing in: the `next` page if it is one of ours, else My plans. */
function nextFrom(search: string): string {
  const next = new URLSearchParams(search).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/login') ? next : '/';
}

const THEME_NEXT: Record<ThemeChoice, ThemeChoice> = { auto: 'light', light: 'dark', dark: 'auto' };
const THEME_LABEL: Record<ThemeChoice, string> = { auto: '🌗 Look: automatic', light: '☀️ Look: light', dark: '🌙 Look: dark' };

function TopBar({ name }: { name: string }) {
  const [theme, setThemeState] = useState<ThemeChoice>(getTheme);
  return (
    <header className="top-bar">
      <Link to="/" className="brand">
        <span aria-hidden="true">🦒</span> Zoo Planner
      </Link>
      <div className="top-right">
        <button
          type="button"
          className="quiet"
          title="Switch between light and dark"
          onClick={() => {
            const next = THEME_NEXT[theme];
            setTheme(next);
            setThemeState(next);
          }}
        >
          {THEME_LABEL[theme]}
        </button>
        <span className="muted">Hi, {name}</span>
        <button type="button" className="quiet" onClick={() => void signOut().catch(() => undefined)}>Sign out</button>
      </div>
    </header>
  );
}
